import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, Shield, UserCheck, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { teamService } from '@/lib/api';

const DEFAULT_PERMISSIONS = {
  manager: {
    can_manage_portfolios: true,
    can_upload_debtors: true,
    can_view_requests: true,
    can_approve_requests: true,
    can_view_metrics: true,
    can_configure_settings: false
  },
  supervisor: {
    can_manage_portfolios: false,
    can_upload_debtors: false,
    can_view_requests: true,
    can_approve_requests: false,
    can_view_metrics: true,
    can_configure_settings: false
  }
};

const PERMISSION_LABELS = {
  can_manage_portfolios: 'Gestionar Carteras',
  can_upload_debtors: 'Subir Clientes',
  can_view_requests: 'Ver Solicitudes',
  can_approve_requests: 'Aprobar Solicitudes',
  can_view_metrics: 'Ver Métricas',
  can_configure_settings: 'Configurar Empresa'
};

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const [newMember, setNewMember] = useState({
    email: '',
    role: 'supervisor',
    permissions: { ...DEFAULT_PERMISSIONS.supervisor }
  });

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await teamService.list();
      setMembers(response.data);
    } catch (error) {
      toast.error('Error al cargar el equipo');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role, isNew = true) => {
    const target = isNew ? 'newMember' : 'editingMember';
    const setter = isNew ? setNewMember : setEditingMember;
    const current = isNew ? newMember : editingMember;
    
    setter({
      ...current,
      role,
      permissions: { ...DEFAULT_PERMISSIONS[role] }
    });
  };

  const handlePermissionChange = (permission, value, isNew = true) => {
    const setter = isNew ? setNewMember : setEditingMember;
    const current = isNew ? newMember : editingMember;
    
    setter({
      ...current,
      permissions: {
        ...current.permissions,
        [permission]: value
      }
    });
  };

  const handleCreate = async () => {
    if (!newMember.email || !newMember.email.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }

    try {
      await teamService.create({
        email: newMember.email,
        role: newMember.role,
        permissions: newMember.permissions
      });
      
      toast.success('Miembro agregado exitosamente');
      setShowCreate(false);
      setNewMember({
        email: '',
        role: 'supervisor',
        permissions: { ...DEFAULT_PERMISSIONS.supervisor }
      });
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar miembro');
    }
  };

  const handleEdit = (member) => {
    setEditingMember({
      ...member,
      permissions: member.permissions || DEFAULT_PERMISSIONS[member.role]
    });
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    try {
      await teamService.update(editingMember.id, {
        role: editingMember.role,
        permissions: editingMember.permissions,
        is_active: editingMember.is_active
      });
      
      toast.success('Miembro actualizado');
      setShowEdit(false);
      setEditingMember(null);
      loadMembers();
    } catch (error) {
      toast.error('Error al actualizar miembro');
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('¿Estás seguro de eliminar este miembro del equipo?')) return;

    try {
      await teamService.delete(memberId);
      toast.success('Miembro eliminado');
      loadMembers();
    } catch (error) {
      toast.error('Error al eliminar miembro');
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      manager: { label: 'Manager', className: 'bg-blue-100 text-blue-800' },
      supervisor: { label: 'Supervisor', className: 'bg-purple-100 text-purple-800' }
    };
    const { label, className } = config[role] || { label: role, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={className}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <div key={`skeleton-${i}`} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Equipo
          </h2>
          <p className="text-sm text-slate-600">
            Gestiona los miembros de tu equipo y sus permisos
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Miembro
        </Button>
      </div>

      {/* Team Members List */}
      {members.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay miembros en el equipo</h3>
          <p className="text-slate-600 mb-4">
            Agrega managers y supervisores para ayudarte a gestionar la cobranza
          </p>
          <Button onClick={() => setShowCreate(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Agregar Primer Miembro
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {members.map(member => (
            <Card key={member.id} className={`p-4 ${!member.is_active ? 'opacity-60 bg-slate-50' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                    {member.picture ? (
                      <img src={member.picture} alt={member.name} className="w-12 h-12 rounded-full" />
                    ) : (
                      <Users className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">
                        {member.name || member.email}
                      </p>
                      {getRoleBadge(member.role)}
                      {!member.is_active && (
                        <Badge variant="outline" className="text-red-600 border-red-200">
                          Inactivo
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{member.email}</p>
                    {member.last_login && (
                      <p className="text-xs text-slate-500">
                        Último acceso: {new Date(member.last_login).toLocaleDateString('es-AR')}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(member)}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Permissions Summary */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(member.permissions || {}).map(([key, value]) => (
                    value && PERMISSION_LABELS[key] && (
                      <span key={key} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                        {PERMISSION_LABELS[key]}
                      </span>
                    )
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Member Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agregar Miembro al Equipo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Email (Gmail)</Label>
              <Input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                placeholder="miembro@gmail.com"
              />
              <p className="text-xs text-slate-500 mt-1">
                El usuario deberá iniciar sesión con Google usando este email
              </p>
            </div>

            <div>
              <Label>Rol</Label>
              <Select
                value={newMember.role}
                onValueChange={(value) => handleRoleChange(value, true)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {newMember.role === 'manager' 
                  ? 'Puede gestionar carteras, clientes y aprobar solicitudes'
                  : 'Solo puede ver información y solicitudes'
                }
              </p>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-slate-500" />
                <Label className="font-medium">Permisos Personalizados</Label>
              </div>
              
              <div className="space-y-3">
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">{label}</span>
                    <Switch
                      checked={newMember.permissions[key] || false}
                      onCheckedChange={(checked) => handlePermissionChange(key, checked, true)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700">
              Agregar Miembro
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Miembro</DialogTitle>
          </DialogHeader>
          
          {editingMember && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-medium">{editingMember.email}</p>
              </div>

              <div>
                <Label>Rol</Label>
                <Select
                  value={editingMember.role}
                  onValueChange={(value) => handleRoleChange(value, false)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <Label className="font-medium">Permisos</Label>
                </div>
                
                <div className="space-y-3">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-slate-700">{label}</span>
                      <Switch
                        checked={editingMember.permissions?.[key] || false}
                        onCheckedChange={(checked) => handlePermissionChange(key, checked, false)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                <div>
                  <p className="font-medium text-red-900">Estado del Miembro</p>
                  <p className="text-sm text-red-700">
                    {editingMember.is_active ? 'Activo - Puede acceder al sistema' : 'Inactivo - No puede acceder'}
                  </p>
                </div>
                <Switch
                  checked={editingMember.is_active}
                  onCheckedChange={(checked) => setEditingMember({ ...editingMember, is_active: checked })}
                />
              </div>

              <Button onClick={handleUpdate} className="w-full bg-teal-600 hover:bg-teal-700">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManager;
